import { forwardRef, memo, useRef, useCallback } from "react";
import SelectionOverlay from "./SelectionOverlay";
import { findNodeById } from "../../utils/svgParser";

// atribut yang boleh diteruskan langsung ke elemen SVG asli
const PASSTHROUGH_ATTRS_BLOCKLIST = new Set(["id", "style"]);

// checker pattern buat nunjukin area transparan, mirip Photoshop/Figma
const CHECKER_STYLE = {
  backgroundImage:
    "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
  backgroundColor: "#f5f5f5",
};

function transformToStyle(t) {
  return {
    transform: `translate(${t.x}px, ${t.y}px) rotate(${t.rotate}deg) scale(${t.scaleX}, ${t.scaleY}) skew(${t.skewX}deg, ${t.skewY}deg)`,
    transformBox: "fill-box",
    transformOrigin: `${50 + t.originX}% ${50 + t.originY}%`,
  };
}

function RenderNode({ node, selectedId, onSelect, onMove, onDragStart, onDragEnd }) {
  const Tag = node.tag;

  const attrs = {};
  Object.entries(node.attrs).forEach(([k, v]) => {
    if (!PASSTHROUGH_ATTRS_BLOCKLIST.has(k)) attrs[k] = v;
  });

  // hold & drag di elemen buat geser posisi (translate). SelectionOverlay (kotak hijau +
  // handle resize/rotate) yang jadi indikator visual seleksi, jadi kita gak perlu lagi
  // outline duplikat di elemen itu sendiri.
  const handleMouseDown = (e) => {
    e.stopPropagation();
    onSelect(node.id);
    if (node.hidden) return;

    const startClientX = e.clientX;
    const startClientY = e.clientY;
    const startX = node.transform.x;
    const startY = node.transform.y;
    let dragging = false;
    const DRAG_THRESHOLD = 2;

    const onMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startClientX;
      const dy = moveEvent.clientY - startClientY;
      if (!dragging && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
        dragging = true;
        onDragStart?.();
      }
      if (dragging) onMove(node.id, startX + dx, startY + dy);
    };
    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      if (dragging) onDragEnd?.();
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  // cegah klik di elemen ikut ke-bubble ke svg (yang punya onClick buat deselect)
  const handleClick = (e) => e.stopPropagation();

  if (node.hidden) return null;

  return (
    <Tag
      {...attrs}
      id={node.id}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      style={{
        ...transformToStyle(node.transform),
        cursor: node.locked ? "default" : "move",
        pointerEvents: node.locked ? "none" : "auto",
      }}
    >
      {node.textContent != null
        ? node.textContent
        : node.children?.map((child) => (
            <RenderNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              onMove={onMove}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          ))}
    </Tag>
  );
}

function Canvas(
  {
    svgMeta,
    elementTree,
    selectedId,
    onSelect,
    onMoveElement,
    backgroundColor,
    isPlaying,
    onScaleChange,
    onRotateChange,
    onDragStart,
    onDragEnd,
  },
  forwardedSvgRef
) {
  const wrapperRef = useRef(null);
  const innerSvgRef = useRef(null);

  // gabung forwardedSvgRef (dipakai parent buat video export) dengan ref internal
  // (dipakai buat measurement selection overlay), keduanya perlu nunjuk ke elemen <svg> yang sama
  const setSvgRef = useCallback(
    (el) => {
      innerSvgRef.current = el;
      if (typeof forwardedSvgRef === "function") forwardedSvgRef(el);
      else if (forwardedSvgRef) forwardedSvgRef.current = el;
    },
    [forwardedSvgRef]
  );

  if (!svgMeta) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#0F0F0F]">
        <p className="text-[#D7C7A1]/50 text-sm">Import file SVG untuk mulai.</p>
      </div>
    );
  }

  const selectedNode = selectedId ? findNodeById(elementTree, selectedId) : null;
  const isTransparent = backgroundColor === null;

  return (
    <div
      className="flex-1 overflow-auto bg-[#0F0F0F] flex items-center justify-center p-8"
      style={{
        backgroundImage:
          "linear-gradient(#145A43 1px, transparent 1px), linear-gradient(90deg, #145A43 1px, transparent 1px)",
        backgroundSize: "24px 24px",
        backgroundPosition: "center",
      }}
    >
      <div ref={wrapperRef} className="relative rounded shadow-lg overflow-hidden">
        <svg
          ref={setSvgRef}
          viewBox={svgMeta.viewBox}
          width={svgMeta.width}
          height={svgMeta.height}
          onClick={() => onSelect(null)}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            display: "block",
            ...(isTransparent ? CHECKER_STYLE : { backgroundColor }),
          }}
        >
          {elementTree.map((node) => (
            <RenderNode
              key={node.id}
              node={node}
              selectedId={selectedId}
              onSelect={onSelect}
              onMove={onMoveElement}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          ))}
        </svg>

        {selectedNode && !isPlaying && !selectedNode.locked && (
          <SelectionOverlay
            svgEl={innerSvgRef.current}
            wrapperEl={wrapperRef.current}
            selectedId={selectedId}
            transform={selectedNode.transform}
            onScaleChange={onScaleChange}
            onRotateChange={onRotateChange}
            onMove={onMoveElement}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        )}
      </div>
    </div>
  );
}

// PENTING: di-memo supaya Canvas TIDAK re-render tiap tick playback (currentTime berubah).
// GSAP mengubah style transform elemen SVG langsung di DOM saat animasi jalan (di luar React).
// Kalau Canvas re-render tiap frame, React menimpa balik style itu ke nilai statis dari
// elementTree, sehingga animasi kelihatan diam / reset tiap frame.
// Selama elementTree, svgMeta, selectedId, dan onSelect referensinya stabil antar render,
// Canvas aman di-skip render-nya dan hasil animasi GSAP tetap kelihatan di layar.
export default memo(forwardRef(Canvas));
